import { useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { appeler } from '../../api/client.ts';
import { useSession } from '../../composants/session.tsx';
import { Entete } from '../../composants/mise-en-page.tsx';
import { Chargement, Erreur } from '../../composants/communs.tsx';
import { Icone } from '../../composants/icones.tsx';
import { construireBarres } from './serie.ts';
import { BlocParBoutique } from './par-boutique.tsx';
import { enNombre } from './outils.ts';
import { TicketDuJour } from './elements/ticket-du-jour.tsx';
import { BlocSurveiller } from './elements/bloc-surveiller.tsx';
import { BlocJours } from './elements/bloc-jours.tsx';
import { BlocStocks } from './elements/bloc-stocks.tsx';
import { BlocActions } from './elements/bloc-actions.tsx';
import { BlocClassement } from './elements/bloc-classement.tsx';
import type { Tableau } from './types.ts';
import './tableau-de-bord.css';

/**
 * Tableau de bord — RG-143, RG-186.
 *
 * L'écran affiche EXACTEMENT ce que le serveur envoie. Les blocs absents de la
 * réponse n'existent pas : un vendeur n'y trouve ni marge ni créances, y
 * compris en inspectant la requête.
 *
 * Composition en REGISTRE plutôt qu'en tableau de bord SaaS générique : le
 * document que ce commerçant lit déjà chaque jour, c'est le ticket que
 * l'appareil imprime au comptoir. Le tableau de bord s'ouvre donc sur un
 * ticket du jour, et le reste s'organise en lignes plutôt qu'en tuiles — un
 * registre ne note que les EXCEPTIONS, jamais ce qui va bien.
 */
export function TableauDeBord(): ReactNode {
  const { boutiqueId, boutiques, peut } = useSession();
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const { data, isPending, error, refetch, isFetching } = useQuery({
    queryKey: ['tableau-de-bord', boutiqueId, date],
    queryFn: () =>
      appeler<Tableau>(
        `/tableau-de-bord?date=${date}` +
          (boutiqueId === null ? '' : `&boutique_id=${boutiqueId}`),
      ),
  });

  const boutiqueChoisie = boutiques.find((boutique) => boutique.id === boutiqueId);
  const boutiqueLibelle =
    boutiqueId === null
      ? 'Toutes boutiques'
      : boutiqueChoisie === undefined
        ? 'Boutique'
        : `${boutiqueChoisie.code} — ${boutiqueChoisie.nom}`;

  return (
    <>
      <Entete
        titre="Tableau de bord"
        sous={sousTitre(data?.portee, boutiqueId)}
        actions={
          <label>
            <span className="visuellement-cache">Journée affichée</span>
            <input
              type="date"
              value={date}
              onChange={(evenement) => {
                setDate(evenement.target.value);
              }}
            />
          </label>
        }
      />

      {isPending && <Chargement />}
      {error !== null && <Erreur erreur={error} />}
      {data !== undefined && (
        <Contenu
          tableau={data}
          date={date}
          boutiqueLibelle={boutiqueLibelle}
          surActualiser={() => void refetch()}
          enActualisation={isFetching}
          // Comparatif par boutique : en consolidé, et avec `rapport.ca.lire`.
          avecComparatif={boutiqueId === null && peut('rapport.ca.lire')}
        />
      )}
    </>
  );
}

/**
 * Le sous-titre DIT la portée des chiffres.
 *
 * Un vendeur ne voit que SES ventes (RG-143). S'il lisait « Activité de la
 * boutique », il croirait que la boutique n'a fait que cela de la journée.
 */
function sousTitre(
  portee: Tableau['portee'] | undefined,
  boutiqueId: string | null,
): string {
  if (portee === 'PERSONNELLE') {
    return 'Vos chiffres du jour';
  }
  return boutiqueId === null
    ? 'Toutes vos boutiques, consolidées'
    : 'Activité de la boutique';
}

function Contenu({
  tableau,
  date,
  boutiqueLibelle,
  surActualiser,
  enActualisation,
  avecComparatif,
}: {
  tableau: Tableau;
  date: string;
  boutiqueLibelle: string;
  surActualiser: () => void;
  enActualisation: boolean;
  avecComparatif: boolean;
}): ReactNode {
  const { session, peut } = useSession();
  const naviguer = useNavigate();

  const total = enNombre(tableau.ventes.total ?? 0);
  const nombre = enNombre(tableau.ventes.nombre ?? 0);
  const panierMoyen = nombre === 0 ? 0 : Math.round(total / nombre);

  const serie = tableau.serie_journaliere ?? [];
  const barres = construireBarres(serie, date);

  const stocks = tableau.stocks_faibles ?? [];
  const anomalies = (tableau.anomalies ?? []).reduce(
    (somme, ligne) => somme + enNombre(ligne.nombre),
    0,
  );
  const meilleures = tableau.meilleures_ventes ?? [];
  const prenom = (session?.utilisateur.nom ?? '').trim().split(' ')[0] ?? '';

  return (
    <>
      {/* ── Bandeau d'accueil : dégradé bleu, sans les chiffres — ils sont
          sur le ticket juste en dessous, une seule fois. ── */}
      <section className="tdb-heros" aria-label="Bienvenue">
        <div>
          <h2 className="tdb-heros__titre">
            Bonjour{prenom === '' ? '' : `, ${prenom}`}
          </h2>
          <p className="tdb-heros__sous">Voici l’activité de la journée.</p>
        </div>
        <button
          type="button"
          className={enActualisation ? 'tdb-actualiser tdb-actualiser--charge' : 'tdb-actualiser'}
          onClick={surActualiser}
        >
          <Icone nom="actualiser" taille={14} />
          Actualiser
        </button>
      </section>

      {/* ── Ticket du jour + registre des exceptions ── */}
      <section className="tdb-principal">
        <TicketDuJour
          boutiqueLibelle={boutiqueLibelle}
          date={date}
          ventesNombre={nombre}
          ventesTotal={total}
          panierMoyen={panierMoyen}
          marge={
            tableau.marge === undefined
              ? undefined
              : {
                  margeBrute: tableau.marge.marge_brute,
                  tauxMargePct: enNombre(tableau.marge.taux_marge_pct),
                }
          }
        />
        <BlocSurveiller stocks={stocks} creances={tableau.creances} anomalies={anomalies} />
      </section>

      {avecComparatif && <BlocParBoutique date={date} />}

      {/* ── Meilleures ventes + alertes de stock, en registre ── */}
      <div className="tdb-deux-colonnes">
        <BlocClassement lignes={meilleures} />
        <BlocStocks
          stocks={stocks}
          surOuvrir={peut('stock.lire') ? () => void naviguer('/stock') : undefined}
        />
      </div>

      <BlocJours barres={barres} />

      <BlocActions />
    </>
  );
}
